import React from 'react'
import styled from '@emotion/styled'
import { isUndefined, isNil, isString } from 'lodash-es'
import { Column, GridApi } from 'ag-grid-community'
import { Locale, Translation } from 'locale'
import {
  ColumnItem,
  ColumnList,
  ColumnSelectorContainer,
  ColumnSelectorOverlay,
  ColumnSelectorTitle,
  FilterContainer,
  FilterTextInput
} from 'view/components/datagrid/toolbar/columnSelectorStyles'
import { CheckboxInput, CheckboxField } from 'view/components/inputs'
import { GridToolbarSettings } from 'store/grid/gridTypes'
import * as mixins from 'view/styles/mixins'
import { deltaIcon } from '../styles/gridIconSet'
import { StandardGridColumnId } from '../gridUtils'

export interface ColumnSelectorProps {
  toolbarSettings: GridToolbarSettings
  gridApi: GridApi | null | undefined
  columns: Column[] | null
  onToolbarSettingsChanged: (toolbarSettings: GridToolbarSettings) => unknown
}

export interface ColumnSelectorState {
  filterValue: string
}

const DeltaIconContainer = styled.span`
  ${mixins.deltaLabel};
`

export class ColumnSelector extends React.Component<ColumnSelectorProps, ColumnSelectorState> {
  state = { filterValue: '' }

  toggle: HTMLElement | null = null

  componentDidMount() {
    this.toggle = document.getElementById('ColumnSelectorToggle')
  }

  render() {
    const { toolbarSettings } = this.props

    const positionY = this.toggle && this.toggle.getBoundingClientRect().bottom - 10

    if (toolbarSettings.showColumnSelector) {
      return (
        <ColumnSelectorOverlay onClick={this.onClose} data-test-id="ColumnSelector-ColumnSelectorOverlay">
          <ColumnSelectorContainer positionY={positionY} onClick={this.onContainerClick}>
            <ColumnSelectorTitle>{Locale.translate(Translation.TC_COLUMNS)}</ColumnSelectorTitle>
            {this.renderFilter()}
            <ColumnList>{this.getColumnItems()}</ColumnList>
          </ColumnSelectorContainer>
        </ColumnSelectorOverlay>
      )
    } else {
      return null
    }
  }

  private renderFilter = () => {
    return (
      <FilterContainer>
        <CheckboxInput checked={this.getAllColumnsVisible()} disabled={false} onClick={this.setAllColumnsVisibility} />
        <FilterTextInput
          value={this.state.filterValue}
          placeholder={Locale.translate(Translation.SC_FILTER)}
          onUpdate={this.handleFilterUpdate}
        />
      </FilterContainer>
    )
  }

  private getColumnItems = (): JSX.Element[] => {
    if (!this.props.columns) {
      return []
    }
    return this.getFilteredColumns().map((column: Column) => {
      const { lockVisible, headerComponentParams, headerName } = column.getColDef()
      const indent = !isUndefined(this.getParentColumn(column))
      const delta = !!headerComponentParams && !!headerComponentParams.delta
      const label = (
        <DeltaIconContainer>
          {delta ? deltaIcon : ''} {headerName || ''}
        </DeltaIconContainer>
      )

      return (
        <ColumnItem
          data-test-id="ColumnSelectorToggle"
          key={column.getColId()}
          indent={indent}
          disabled={lockVisible || indent}
          onClick={() => this.columnVisibilityHandler(column)}>
          <CheckboxField
            label={label}
            isChecked={column.isVisible()}
            disabled={lockVisible || indent}
            onUpdate={() => this.columnVisibilityHandler(column)}
            dataTestId={`ColumnSelectorToggle-${column.getColId()}-Column`}
          />
        </ColumnItem>
      )
    })
  }

  private handleFilterUpdate = (filterValue: string) => {
    this.setState({ filterValue })
  }

  private getParentColumn = (column: Column) => {
    const headerComponentParams = column.getColDef().headerComponentParams
    return headerComponentParams && headerComponentParams.parent
  }

  private getChildColumns = (column: Column) => {
    const headerComponentParams = column.getColDef().headerComponentParams
    return headerComponentParams && headerComponentParams.children
  }

  private getAllColumnsVisible = () => {
    const { columns } = this.props
    return (
      !!columns &&
      this.getFilteredColumns()
        .filter(column => isUndefined(this.getParentColumn(column)))
        .reduce((allVisible, column) => allVisible && column.isVisible(), true)
    )
  }

  // extremely janky due to the number of columns
  private setAllColumnsVisibility = () => {
    const { columns } = this.props
    if (columns) {
      const isAllColumnsVisible = this.getAllColumnsVisible()
      this.getFilteredColumns()
        .filter(column => isUndefined(this.getParentColumn(column)) && !column.getColDef().lockVisible)
        .forEach(column => this.columnVisibilityHandler(column, !isAllColumnsVisible))
    }
  }

  private toggleColumnVisibility = (column: Column, visible: boolean) => {
    const { gridApi, toolbarSettings } = this.props
    if (gridApi) {
      gridApi.setColumnsVisible([column], visible)

      const childColumns = this.getChildColumns(column)
      if (!isUndefined(childColumns)) {
        gridApi.setColumnsVisible([childColumns[0]], visible)
        if (toolbarSettings.showDelta) {
          gridApi.setColumnsVisible([childColumns[1]], visible)
        }
      }
    }
  }

  private columnVisibilityHandler = (column: Column, visible: boolean = !column.isVisible()) => {
    const { cellRendererParams } = column.getColDef()

    if (isNil(cellRendererParams) || isNil(cellRendererParams.originalColId)) {
      return this.toggleColumnVisibility(column, visible)
    }

    const counterpartyCols = this.getCounterPartyColumnsByOriginalColId(cellRendererParams.originalColId)

    counterpartyCols.forEach(counterpartyCol => this.toggleColumnVisibility(counterpartyCol, visible))
  }

  private getFilteredColumns = () => {
    const columns = this.getUniqueColumns()
    const { filterValue } = this.state

    return columns
      .filter(column => {
        const colDef = column.getColDef()
        const headerName = colDef.headerName || Locale.translate(Translation.TC_EMPTY_HEADER)
        return filterValue === ''
          ? headerName !== Locale.translate(Translation.TC_EMPTY_HEADER)
          : headerName.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())
      })
      .filter(column => column.getColId() !== StandardGridColumnId.GROUPING)
  }

  private getUniqueColumns = () => {
    const { columns } = this.props

    if (columns) {
      return columns.reduce<Column[]>((result, column) => {
        const { cellRendererParams } = column.getColDef()

        if (isNil(cellRendererParams)) {
          return result.concat(column)
        }

        const { originalColId } = cellRendererParams

        if (isNil(originalColId)) {
          return result.concat(column)
        }

        const exists = result.find(col => {
          const colDef = col.getColDef()
          return colDef.cellRendererParams && colDef.cellRendererParams.originalColId === originalColId
        })

        if (exists) {
          return result
        }

        return result.concat(column)
      }, [])
    } else {
      return []
    }
  }

  private getCounterPartyColumns = () => {
    const { columns } = this.props
    if (columns) {
      return columns.filter(column => {
        const { cellRendererParams } = column.getColDef()
        return !isNil(cellRendererParams) && isString(cellRendererParams.originalColId)
      })
    } else {
      return []
    }
  }

  private getCounterPartyColumnsByOriginalColId = (colId: string) => {
    const columns = this.getCounterPartyColumns()
    return columns.filter(column => {
      const { cellRendererParams } = column.getColDef()

      if (isNil(cellRendererParams)) {
        return false
      }

      return colId === cellRendererParams.originalColId
    })
  }

  private onContainerClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  private onClose = () => {
    const { toolbarSettings, onToolbarSettingsChanged } = this.props

    const updatedToolbarSettings: GridToolbarSettings = {
      ...toolbarSettings,
      showColumnSelector: false
    }

    onToolbarSettingsChanged(updatedToolbarSettings)
  }
}
